"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useInView, useIsomorphicLayoutEffect, useMotionValue, useReducedMotion, type MotionValue } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import { Pause, Play } from "lucide-react";

import { cn } from "@/lib/utils";
import { journeyCopy } from "./journeyMediaCopy";
import styles from "./journeyMedia.module.scss";

export const journeyAsset = (name: string) => `/home-v2/journey/motion/${name}`;

// Artwork coordinates use the 620 × 460 Figma canvas, independently of page rem scaling.
export const u = (value: number) => `calc(var(--u) * ${value})`;

export function Artwork({ name, className }: { name: string; className?: string }) {
  return <img src={journeyAsset(name)} alt="" loading="lazy" decoding="async" draggable={false} className={className} />;
}

export interface JourneyTimeline {
  progress: MotionValue<number>;
  running: boolean;
  reducedMotion: boolean;
}

export default function JourneyMediaFrame({
  variant, language, length, interval = 2600, durations, className, description, children,
}: {
  variant: "challenge" | "funding" | "elite";
  language?: string;
  length: number;
  interval?: number;
  durations?: number[];
  className?: string;
  description?: string;
  children: (index: number, timeline: JourneyTimeline) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.35 });
  const motionPreference = useReducedMotion();
  const [reducedMotion, setReducedMotion] = useState(false);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(true);
  const progress = useMotionValue(0);
  const elapsed = useRef(0);
  const previousIndex = useRef(0);
  const copy = journeyCopy(language);
  const currentIndex = reducedMotion ? (variant === "elite" ? 0 : length - 1) : index;
  const background = variant === "funding" && currentIndex === length - 1 ? "payout" : variant;
  const running = inView && !reducedMotion && !paused && visible;
  const duration = durations?.[index] ?? (variant === "funding" && index === length - 1 ? interval * 2 : interval);

  useEffect(() => setReducedMotion(Boolean(motionPreference)), [motionPreference]);

  useIsomorphicLayoutEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    // Avoid size containment here: every artwork layer must move with its sticky card.
    const measure = () => frame.style.setProperty("--u", `${frame.getBoundingClientRect().width / 620}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (previousIndex.current !== index) {
      elapsed.current = 0;
      progress.set(0);
      previousIndex.current = index;
    }
    if (reducedMotion) {
      progress.set(1);
      return;
    }
    if (!running) return;
    let last = performance.now();
    let frame: number;
    const tick = (now: number) => {
      elapsed.current += now - last;
      last = now;
      progress.set(Math.min(elapsed.current / duration, 1));
      if (elapsed.current >= duration) {
        setIndex((value) => (value + 1) % length);
      } else {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running, reducedMotion, length, duration, index, progress]);

  return (
    <div ref={ref} lang={copy.locale} className={cn("w-full lg:w-[77.5rem]", styles.frame, styles[variant], className)} data-journey-media={variant} data-stage={currentIndex} data-running={running} data-reduced-motion={reducedMotion}>
      <div className={styles.canvas} role="img" aria-label={stegaClean(description) || copy.descriptions[["challenge", "funding", "elite"].indexOf(variant)]}>
        <div aria-hidden="true">
          <Artwork name={`${background}-bg.svg`} className={styles.background} />
          {variant === "funding" && <Artwork name="funding-art.webp" className={styles.fundingArt} />}
          <Artwork name="grain.webp" className={styles.grain} />
          {variant !== "funding" && <Artwork name={`${variant}-rings.svg`} className={styles.background} />}
          {children(currentIndex, { progress, running, reducedMotion })}
        </div>
      </div>
      {!reducedMotion && (
        <button type="button" className={styles.control} onClick={() => setPaused(!paused)} aria-label={paused ? copy.play : copy.pause} aria-pressed={paused}>
          {paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
        </button>
      )}
    </div>
  );
}
